// src/i18n/index.js - English or Arabic for Shift (v135.0). Bundled to dist/i18n.js (global I18N), loaded FIRST in
// index.html and admin.html (and later chat, leader and scan). NEVER in dev.html (Matt, 15 Oct 2026: "I will not need
// it in dev").
//
// Matt, 15 Oct 2026: "I want the ability to use shift in english and a drop down to change to arabic." Decisions the
// same day: Claude drafts the Arabic, staff feedback corrects it; the whole layout mirrors right-to-left; digits stay
// 0-9; names and system terms stay English; the choice is remembered PER DEVICE (a shared till's choice applies to
// everyone on it); staff app first, then admin. What people type (emails, chat, notes, names) is never translated.
//
// HOW: a translator layer, not a rewrite (the Virtual Store's approach, games/virtual-store/src/ar.js). With Arabic
// on, every text node and the placeholder / title / aria-label of every element is looked up in the catalogue
// (src/i18n/ar.js, keyed by the English as shown) and swapped; new content is caught by a MutationObserver, whose
// callback runs before the browser paints, so English does not flash. Anything not in the catalogue stays English,
// so a gap is never a break. The English is kept beside each swapped node, so switching back is exact.
// Skipped: script, style, inputs and textareas (what people type), contenteditable, and anything inside
// translate="no" or data-no-i18n (chat messages, email bodies, names).
//
// ON STAGING (or with ?i18n_debug=1) the English left on screen while Arabic is on is collected: I18N.missing() in the
// console lists it, which is how each phase finds its gaps.
//
// v136.8 (16 Oct 2026, the page-weight audit): this file is the ENGINE only (a few KB). The staff catalogue
// (src/i18n/ar.js, about 290 KB) is its own bundle, dist/i18n-ar.js (src/i18nCatalog), and hands itself over with
// installCatalog(); the admin app's catalogue (dist/i18n-admin.js) still uses extend(). In English none of it is
// ever used, so none of it is downloaded:
//   - an ARABIC device: the page's inline loader, straight after this script, calls writeCatalogs(), which
//     document.writes the catalogue tags while the page is still being read, so they run before anything is drawn
//     (no English flash), exactly as when they were plain tags;
//   - someone CHOOSES Arabic on an English page: setLang('ar') fetches them (loadCatalogs), and each translates the
//     page as it lands. Until then the page stays as it was.
// Each page names the catalogues it uses with useCatalogs([...]) in that inline loader, never by sniffing text.
// The watcher (MutationObserver) is attached only while Arabic is on; English costs nothing.

export const LANG_KEY = 'shift_lang';
export const SWITCH_KEY = 'lang_ar_enabled';   // app_config: Head Office shows the dropdown to stores (off by default)
const ATTRS = ['placeholder', 'title', 'aria-label'];
const SKIP_TAGS = { SCRIPT: 1, STYLE: 1, TEXTAREA: 1, INPUT: 1, NOSCRIPT: 1, CODE: 1, PRE: 1, SVG: 1 };
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;600;700;800&display=swap';

// The catalogue the page uses: empty until a catalogue bundle hands one over (installCatalog / extend).
let AR = {};
let AR_PATTERNS = [];

let _lang = 'en';
let _observer = null;
const _orig = new WeakMap();        // text node -> its English
const _origAttr = new WeakMap();    // element -> { attr: English }
const _missing = new Set();
let _debug = false;

// v136.8: the answers already worked out for the page's catalogue, keyed by the normalised text ("not found" is
// remembered too). lookup() is pure for a given catalogue, so this is cleared whenever the catalogue changes
// (installCatalog, extend) and on every setLang. Capped: cleared when full. lookup() itself, the exported function,
// is never cached (tests and callers that pass their own catalogue get a fresh answer every time).
const CACHE_MAX = 5000;
const _cache = new Map();
const NOT_FOUND = {};
function cachedLookup(text) {
  const k = normKey(text);
  const hit = _cache.get(k);
  if (hit !== undefined) return hit === NOT_FOUND ? null : hit;
  const v = lookup(k);
  if (_cache.size >= CACHE_MAX) _cache.clear();
  _cache.set(k, v == null ? NOT_FOUND : v);
  return v;
}
export function clearCache() { _cache.clear(); }

// ── pure ──────────────────────────────────────────────────────────────────────
export function normKey(s) { return String(s == null ? '' : s).replace(/\s+/g, ' ').trim(); }

// The Arabic for one piece of English, or null. Exact match first, then the patterns (text with a number or a name).
export function lookup(english, cat, patterns) {
  const c = cat || AR, p = patterns || AR_PATTERNS;
  const k = normKey(english);
  if (!k) return null;
  if (Object.prototype.hasOwnProperty.call(c, k)) return c[k];
  // v136.8: text with no Latin letter at all (a time, a number, a date, Arabic already) cannot match anything
  // below: every pattern needs a Latin letter, and so do the decorated and joined forms (pinned by a test over every
  // catalogue's patterns and keys), so it skips the pattern scan.
  if (!/[A-Za-z]/.test(k)) return null;
  // v135.3: a message joined to its reason (see below). When BOTH halves are known this wins over a broad pattern
  // such as /^Save failed: (.+)$/, which would leave a known reason in English.
  const top = !(arguments[3] > 0);
  const j = top ? k.match(/^(.{2,80}?)(: | — | - )(.+)$/) : null;
  const jHead = j ? lookup(j[1], c, p, 1) : null;
  const jRest = jHead != null ? lookup(j[3], c, p, 1) : null;
  const joined = jHead != null ? jHead + j[2] : null;   // the separator as written
  if (joined != null && jRest != null) return joined + jRest;
  for (let i = 0; i < p.length; i++) {
    const re = p[i][0];
    if (re.test(k)) { re.lastIndex = 0; return k.replace(re, p[i][1]); }
  }
  // The words inside a leading emoji, symbol or number, and a trailing colon, ellipsis or full stop: '📧 Email',
  // '3 to confirm', 'Store:'. The decoration is kept as it is; only the words are looked up.
  const m = k.match(/^([^A-Za-z]*?)([A-Za-z][\s\S]*?)([\s:.!?…]*)$/);
  if (m && (m[1] || m[3])) {
    const core = m[2];
    const withEnd = core + m[3].trim();   // a key may carry its own full stop ("... is available.")
    if (m[3].trim() && Object.prototype.hasOwnProperty.call(c, withEnd)) return m[1] + c[withEnd];
    if (Object.prototype.hasOwnProperty.call(c, core)) return m[1] + c[core] + m[3];
  }
  // v135.3: a message joined to its reason, 'Save failed: Store not found' or 'TOIL NOT saved - the server refused the
  // write'. Each half is looked up on its own; the first half must be known, the reason is translated when it can be
  // and otherwise kept as it is (a server message we have not met yet), so the label at least reads in Arabic.
  if (joined != null) return joined + j[3];
  return null;
}

// t('English') for strings that never reach the page as text (alert, confirm, document.title...).
export function t(english) {
  if (_lang !== 'ar') return english;
  const a = cachedLookup(english);
  return a == null ? english : a;
}

// Is there English worth reporting in this text (for the staging finder)?
// Already-translated text (it has Arabic letters, perhaps with a kept term like KPI) and names kept in English are not gaps.
export function looksEnglish(s) {
  const k = normKey(s);
  if (!/[A-Za-z]{2,}/.test(k) || /[؀-ۿ]/.test(k)) return false;
  return !/^(Shift|Jack|TOIL|KPI|EOD|SVR|AED|QAR|ALGT|TEAL|PIN|OK|AM|PM|MTD|WTD|QTD|YTD|CSV|The Entertainer|Early Learning Centre)$/.test(k);
}

// ── the DOM pass ──────────────────────────────────────────────────────────────
function skipped(el) {
  for (let n = el; n && n.nodeType === 1; n = n.parentNode) {
    if (SKIP_TAGS[n.nodeName.toUpperCase()]) return true;
    if (n.getAttribute && (n.getAttribute('translate') === 'no' || n.hasAttribute('data-no-i18n'))) return true;
    if (n.isContentEditable) return true;
  }
  return false;
}
function doText(node) {
  const cur = node.nodeValue;
  if (!cur || !/\S/.test(cur)) return;
  const parent = node.parentNode;
  if (!parent || parent.nodeType !== 1 || skipped(parent)) return;
  if (_lang === 'ar') {
    const ar = cachedLookup(cur);
    if (ar != null) {
      if (normKey(cur) === normKey(ar)) return;
      // v135.2: an <option> with no value attribute submits its TEXT, so translating it would save the Arabic word
      // (a day off, a delivery day, a role). It keeps its English as its value first; nothing else changes.
      if (parent.nodeName.toUpperCase() === 'OPTION' && !parent.hasAttribute('value')) parent.setAttribute('value', normKey(cur));
      _orig.set(node, cur);
      const lead = (cur.match(/^\s*/) || [''])[0], tail = (cur.match(/\s*$/) || [''])[0];
      node.nodeValue = lead + ar + tail;
    } else if (_debug && looksEnglish(cur)) {
      _missing.add(normKey(cur));
    }
  } else if (_orig.has(node)) {
    node.nodeValue = _orig.get(node);
    _orig.delete(node);
  }
}
// An input or textarea keeps what is typed, but its placeholder / title / aria-label are labels and ARE translated.
function attrsSkipped(el) {
  if (el.getAttribute('translate') === 'no' || el.hasAttribute('data-no-i18n') || el.isContentEditable) return true;
  const tag = el.nodeName.toUpperCase();
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && SKIP_TAGS[tag]) return true;
  return !!(el.parentNode && el.parentNode.nodeType === 1 && skipped(el.parentNode));
}
function doAttrs(el) {
  if (!el.getAttribute || attrsSkipped(el)) return;
  for (let i = 0; i < ATTRS.length; i++) {
    const a = ATTRS[i];
    const v = el.getAttribute(a);
    if (v == null || !/\S/.test(v)) continue;
    let store = _origAttr.get(el);
    if (_lang === 'ar') {
      const ar = cachedLookup(v);
      if (ar != null && ar !== v) {
        if (!store) { store = {}; _origAttr.set(el, store); }
        store[a] = v;
        el.setAttribute(a, ar);
      } else if (ar == null && _debug && looksEnglish(v)) {
        _missing.add(normKey(v));
      }
    } else if (store && store[a] != null) {
      el.setAttribute(a, store[a]);
      delete store[a];
    }
  }
}
export function translateTree(root) {
  if (!root) return;
  if (root.nodeType === 3) { doText(root); return; }
  if (root.nodeType !== 1 && root.nodeType !== 9 && root.nodeType !== 11) return;
  if (root.nodeType === 1) { doAttrs(root); if (skipped(root)) return; }
  const doc = root.ownerDocument || root;
  const walker = doc.createTreeWalker(root, 5 /* SHOW_ELEMENT | SHOW_TEXT */, {
    acceptNode: function (n) {
      if (n.nodeType === 1) {
        const tag = n.nodeName.toUpperCase();
        if (n.getAttribute && (n.getAttribute('translate') === 'no' || n.hasAttribute('data-no-i18n'))) return 2;   // reject the subtree
        if (SKIP_TAGS[tag] && tag !== 'INPUT' && tag !== 'TEXTAREA') return 2;   // inputs: their labels are translated, their text never
      }
      return 1;
    },
  });
  let n = walker.nextNode();
  while (n) {
    if (n.nodeType === 3) doText(n); else doAttrs(n);
    n = walker.nextNode();
  }
}

// v136.8: attached only while Arabic is on (setLang), detached again in English.
function observe() {
  if (_observer || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
  _observer = new MutationObserver(function (list, obs) {
    if (_lang !== 'ar') return;   // English costs nothing: setLang('en') restores everything in one pass
    for (let i = 0; i < list.length; i++) {
      const m = list[i];
      if (m.type === 'characterData') doText(m.target);
      else if (m.type === 'attributes') doAttrs(m.target);
      else for (let j = 0; j < m.addedNodes.length; j++) translateTree(m.addedNodes[j]);
    }
    // v136.8: the Arabic just written fired the watcher again; those records are our own output, so drop them
    // (re-reading them found nothing, after a full pattern scan of every swapped text).
    if (obs && obs.takeRecords) obs.takeRecords();
  });
  _observer.observe(document.documentElement, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}
function unobserve() {
  if (!_observer) return;
  _observer.disconnect();
  _observer = null;
}
// After a full pass over the page: what the watcher queued meanwhile is the pass's own writes, or changes the pass
// has just covered.
function settle() { if (_observer && _observer.takeRecords) _observer.takeRecords(); }
export function isObserving() { return !!_observer; }

// ── the language ─────────────────────────────────────────────────────────────
function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
function lsSet(k, v) { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (e) { /* private window: fine */ } }

export function getLang() { return _lang; }

function loadFont() {
  if (typeof document === 'undefined' || document.getElementById('i18nArabicFont')) return;
  try { if (/HappyDOM|jsdom/i.test(navigator.userAgent)) return; } catch (e) { /* no navigator */ }   // tests: no downloads
  const l = document.createElement('link');
  l.id = 'i18nArabicFont'; l.rel = 'stylesheet'; l.href = FONT_HREF;
  (document.head || document.documentElement).appendChild(l);
}

export function setLang(lang, opts) {
  const next = lang === 'ar' ? 'ar' : 'en';
  _lang = next;
  _cache.clear();
  if (!(opts && opts.noSave)) lsSet(LANG_KEY, next === 'ar' ? 'ar' : null);
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.setAttribute('lang', next);
    root.setAttribute('dir', next === 'ar' ? 'rtl' : 'ltr');
    if (next === 'ar') {
      loadFont();
      // v136.8: the catalogues, if this page has not got them yet. Not at start-up: there the page's inline loader
      // writes them in while the page is read (writeCatalogs), and a late check covers a page without one (boot).
      if (!(opts && opts.boot)) loadCatalogs();
    }
    translateTree(document.documentElement);
    syncPickers();
    if (next === 'ar') { observe(); settle(); } else unobserve();
  }
  return next;
}

// ── the catalogues (v136.8) ──────────────────────────────────────────────────
// The URLs of the catalogue bundles this page uses, in order (the staff catalogue first), set by the page's inline
// loader: I18N.useCatalogs(['dist/i18n-ar.js', 'dist/i18n-admin.js']).
let _catalogs = [];
const _requested = {};
let _hasCatalog = false;
export function useCatalogs(list) {
  _catalogs = Array.isArray(list) ? list.filter(function (s) { return typeof s === 'string' && s; }) : [];
  return _catalogs.slice();
}
export function catalogs() { return _catalogs.slice(); }
export function hasCatalog() { return _hasCatalog; }
function tagPresent(src) {
  try { return !!document.querySelector('script[src="' + src + '"]'); } catch (e) { return false; }
}
// An Arabic device, at start-up: called by the inline loader straight after this script, while the page is still
// being read, so the written tags load and run in order before anything after them is drawn (no English flash).
// Called any later, it fetches them instead.
export function writeCatalogs() {
  if (typeof document === 'undefined') return 0;
  if (document.readyState !== 'loading' || typeof document.write !== 'function') return loadCatalogs();
  let html = '', n = 0;
  _catalogs.forEach(function (src) {
    if (_requested[src] || tagPresent(src)) return;
    _requested[src] = 1;
    html += '<script src="' + src + '"></scr' + 'ipt>';
    n++;
  });
  if (html) document.write(html);
  return n;
}
// Someone chose Arabic on a page that has not got the catalogues: fetch them, in the page's order. Each one translates
// the page as it lands (installCatalog / extend); meanwhile the page stays as it was. A failed fetch can be tried
// again by the next choice of Arabic.
export function loadCatalogs() {
  if (typeof document === 'undefined') return 0;
  let n = 0;
  _catalogs.forEach(function (src) {
    if (_requested[src] || tagPresent(src)) return;
    _requested[src] = 1;
    const s = document.createElement('script');
    s.src = src;
    s.async = false;   // run in the order added: the staff catalogue first
    s.onerror = function () { delete _requested[src]; if (s.parentNode) s.parentNode.removeChild(s); };
    (document.head || document.documentElement).appendChild(s);
    n++;
  });
  return n;
}

// The staff catalogue hands itself over (src/i18nCatalog, dist/i18n-ar.js). It wins over anything a page catalogue
// added first with extend(), exactly as when the staff catalogue was built in: the core wins a shared key, and its
// patterns come first. The object is adopted, not copied. Returns how many strings it brought.
export function installCatalog(strings, patterns) {
  if (!strings || typeof strings !== 'object' || strings === AR) return 0;
  const prev = AR, prevPats = AR_PATTERNS;
  AR = strings;
  AR_PATTERNS = Array.isArray(patterns) ? patterns : [];
  Object.keys(prev).forEach(function (k) {
    if (!Object.prototype.hasOwnProperty.call(AR, k)) AR[k] = prev[k];
  });
  prevPats.forEach(function (p) { AR_PATTERNS.push(p); });
  _hasCatalog = true;
  _cache.clear();
  if (_lang === 'ar' && typeof document !== 'undefined' && document.documentElement) { translateTree(document.documentElement); settle(); }
  return Object.keys(strings).length;
}

// The dropdown. Any element marked data-lang-picker becomes one; it shows only when the switch allows Arabic.
let _allowed = false;
export function pickerHtml(lang) {
  return '<select class="lang-picker" aria-label="Language" data-no-i18n onchange="I18N.setLang(this.value)">' +
    '<option value="en"' + (lang === 'en' ? ' selected' : '') + '>English</option>' +
    '<option value="ar"' + (lang === 'ar' ? ' selected' : '') + ' lang="ar">العربية</option></select>';
}
export function syncPickers() {
  if (typeof document === 'undefined') return;
  const slots = document.querySelectorAll('[data-lang-picker]');
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i];
    if (!_allowed) { s.innerHTML = ''; s.hidden = true; continue; }
    s.hidden = false;
    const sel = s.querySelector('select.lang-picker');
    if (sel) sel.value = _lang; else s.innerHTML = pickerHtml(_lang);
  }
}

// Staging always shows the dropdown (that is where each phase is checked); live, Head Office's switch decides.
export function isStagingHost(host) { return /staging|localhost|127\.0\.0\.1/.test(String(host || '')); }

// Turn the dropdown on or off. Off also returns a device that had chosen Arabic to English.
export function allow(on) {
  _allowed = !!on;
  if (!_allowed && _lang === 'ar') setLang('en', { noSave: false });
  else syncPickers();
}

// More of the catalogue, from a second bundle: the admin app's Arabic lives in dist/i18n-admin.js (src/i18nAdmin) so
// the staff app, which loads this file before it draws anything, never downloads it. A key already in the catalogue
// keeps its Arabic (the core wins, as in ar.js), so a shared word means the same in both apps. Returns how many were added.
export function extend(strings, patterns) {
  let added = 0;
  if (strings && typeof strings === 'object') {
    Object.keys(strings).forEach(function (k) {
      if (!Object.prototype.hasOwnProperty.call(AR, k)) { AR[k] = strings[k]; added++; }
    });
  }
  if (Array.isArray(patterns)) patterns.forEach(function (p) { AR_PATTERNS.push(p); });
  _cache.clear();
  if (_lang === 'ar' && typeof document !== 'undefined' && document.documentElement) { translateTree(document.documentElement); settle(); }
  return added;
}

export function missing() { return Array.from(_missing).sort(); }
export function coverage() { return { strings: Object.keys(AR).length, patterns: AR_PATTERNS.length }; }

// Read the switch the way the short staff bar reads its own (the tokenless /api/config). Missing or false = off.
// v135.2: true or false only when the Worker ANSWERED; null when it could not be asked (offline till, a network blip,
// a page without db.js). Unknown must change nothing: before this, a failed read counted as "off" and sent an Arabic
// device back to English and forgot its choice.
export async function readSwitch(fetchImpl) {
  try {
    const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
    if (!base) return null;
    const r = await (fetchImpl || fetch)(base + '/api/config?key=' + SWITCH_KEY, { cache: 'no-store' });
    if (!r.ok) return null;
    const d = await r.json();
    if (!d || typeof d !== 'object') return null;
    return d.value === true || d.value === 'true';
  } catch (e) { return null; }
}
// What the page does with that answer: on or off as told; unknown keeps the device as it is (an Arabic device keeps its
// dropdown, so it can still switch back).
export function applySwitch(on) {
  if (on === null || on === undefined) { if (_lang === 'ar') allow(true); return; }
  allow(on);
}

// Start: apply the saved choice at once (before the page paints), then confirm the switch.
export function boot() {
  if (typeof document === 'undefined') return;
  let host = '', search = '';
  try { host = location.hostname; search = location.search; } catch (e) { /* no location */ }
  _debug = isStagingHost(host) || /[?&]i18n_debug=1/.test(search);
  const saved = lsGet(LANG_KEY) === 'ar' ? 'ar' : 'en';
  // v136.8: Arabic attaches the watcher (in setLang); English never does. The catalogues are the inline loader's job
  // here (boot: true), as it can still write them into the page.
  if (saved === 'ar') setLang('ar', { noSave: true, boot: true });
  let self = '';
  try { self = (document.currentScript && document.currentScript.getAttribute('src')) || ''; } catch (e) { /* none */ }
  const later = function () {
    // A page whose inline loader is missing, or did not run (an older cached copy of the page beside this newer
    // translator): fetch the staff catalogue from beside this script, so an Arabic device never loses its Arabic.
    if (_lang === 'ar') {
      if (!_catalogs.length && !_hasCatalog && /i18n\.js$/.test(self)) useCatalogs([self.replace(/i18n\.js$/, 'i18n-ar.js')]);
      loadCatalogs();
    }
    if (isStagingHost(host)) { allow(true); return; }
    readSwitch().then(applySwitch);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', later); else later();
}

const api = { t, setLang, getLang, allow, missing, coverage, translateTree, lookup, normKey, boot, extend, installCatalog, useCatalogs, catalogs, hasCatalog, writeCatalogs, loadCatalogs, isObserving, clearCache, LANG_KEY, SWITCH_KEY };
if (typeof window !== 'undefined') {
  window.I18N = api;   // (esbuild's globalName then sets I18N to this module's exports: the same functions)
  boot();
}
export default api;
